import React, {
  FC,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  ColumnDef,
  ColumnSizingState,
  FilterFn,
  Row,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import GridItem from '../../layout/GridComponents/GridItem';

import { useDispatch } from 'react-redux';
import { Button } from '@mui/material';

import { addComponentToPdfExport } from '../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../types/redux/pdfExports/FileExportsStore';
import { ultraTableStyles } from './styles/ultratable.styles';
import { RankingInfo } from '@tanstack/match-sorter-utils';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import ExportButton from '../../feedback/ExportButton';
import { DraggableTableHeader } from './components/DraggableTableHeader';
import { useVirtualizer } from '@tanstack/react-virtual';
import { RowData } from '@tanstack/react-table';
import {
  createDefaultColumnVisibility,
  customSortingFn,
  fuzzyFilter,
  getColumnOrder,
} from './utils/ultratable.utils';
import { getCommonPinningStyles } from './styles/extras.styles';
import { countTrueValues } from './utils/buildUltraTableData';
import { UltraTablePreset } from './types/presets.types';
import { ColumnFields } from './types/column.types';
import { Legend } from './components/Legend';
import { SortingFn } from '@tanstack/react-table';
import { EditableFields } from './types/editable.types';
import PresetSelectionBtn from './components/buttons/PresetSelectionBtn';
import GenericDropdown from '../../ui/GenericDropDown';
import PortalTools from './components/buttons/PortalTools';
import SearchBar from '../../ui/inputs/SearchBar';
import ColumnPopupSelector from './components/ColumnPopupSelector';
import RaptorLoading from '../../feedback/RaptorLoading';
import { ExpandedState } from '@tanstack/react-table';
import { isEqual } from 'lodash';

declare module '@tanstack/react-table' {
  //add fuzzy filter to the filterFns
  interface FilterFns {
    fuzzy?: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }

  interface SortingFns {
    customSortingFn?: SortingFn<unknown>; // Make customSortingFn optional
  }

  interface TableMeta<TData extends RowData> {
    updateData: (
      rowIndex: number,
      columnId: string,
      value: unknown,
      resetOld?: boolean,
    ) => void;
  }
}

function useSkipper() {
  const shouldSkipRef = React.useRef(true);
  const shouldSkip = shouldSkipRef.current;

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = React.useCallback(() => {
    shouldSkipRef.current = false;
  }, []);

  React.useEffect(() => {
    shouldSkipRef.current = true;
  });

  return [shouldSkip, skip] as const;
}

type UltraTableProps<T> = {
  isAggregatePending?: boolean;
  title?: string;
  tableData: TableData<T>;

  getRowCanExpand: (row: Row<T>) => boolean;
  pdfConfig?: {
    pdfExportGroupName?: string;
    pdfExportGroupOrder?: number;
    pdfIdentifier: string;
    pdfTitle?: string;
    pdfFileName?: string;
  };
  positionDate?: string;
  nestedComponent?: FC<{
    data: any;
    rowIndex: number;
    initialOffset: () => number;
    scrollMargin: number;
    getScrollElement: () => HTMLDivElement;
  }>;

  // isEditable?: {
  //   fn: GeneralFunctionType; // General function to handle the update of a cell
  // };

  isEditable?: boolean; // Whether the table is editable
  onDataComparison?: (isDataEqualToOriginal: boolean) => void; // Function to handle the comparison of the data // TODO: Rethink better - We need to reduce state inconsistancies and figure a way to make editable cells set pending/updated states to parent component

  toolBarBtns?: JSX.Element[]; // An extensible toolbar that can have default buttons within ultracontext and custom buttons passed in via children

  actionBtns?: React.ReactElement<HTMLButtonElement>[]; // Array of action buttons to be displayed in the toolbar - TODO: Rethink better
  presets?: UltraTablePresetConfig; // Handles the preset capibilities of the table
  columnSelector?: boolean; // Whether the column selector is enabled
  exclusions?: string[]; // This is a quick workaround to hardcode columns that are never filtered out

  isFetching?: boolean; // Whether the table is fetching data
  resetExpansion?: boolean | string | number; // Value that when changed will reset all expanded rows
  tableContainerClassName?: string; // Optional className to override tableContainer styles
};

type PresetDisplay = 'dropdown' | 'buttons';

export type UltraTablePresetConfig = {
  default: UltraTablePreset;
  allPresets: UltraTablePreset[];
  display: PresetDisplay;
  context?: UltraTablePreset;
};

export type TableData<T> = {
  data: T[];
  columns: ColumnDef<T>[];
};

const getOldValue = (value: any) => {
  if (typeof value === 'object' && value !== null && 'old' in value) {
    return value.old;
  }
  return value;
};

// The main positions table component, which will be exported from the file
const UltraTable = <T extends EditableFields>({
  title,
  tableData,
  getRowCanExpand,
  pdfConfig,
  nestedComponent: NestedComponent,
  isEditable,
  actionBtns,
  toolBarBtns,
  presets,
  columnSelector,
  onDataComparison,
  positionDate,
  exclusions,
  isFetching,
  isAggregatePending,
  resetExpansion,
  tableContainerClassName,
}: UltraTableProps<T>): React.ReactElement => {
  const classes = ultraTableStyles();
  const dispatch = useDispatch();

  // Not ideal solution for identifying selected preset but only general solution I could think of for now
  const [selectedPreset, setSelectedPreset] = useState(presets?.default);

  const [globalFilter, setGlobalFilter] = React.useState('');

  const { columns, data: originalData } = {
    columns: tableData.columns,
    data: tableData.data,
  };


  const defaultColumnVisibility = useMemo(() => {
    return createDefaultColumnVisibility(columns, presets);
  }, [presets, columns]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    defaultColumnVisibility,
  );


  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  // Sync visibility when the titlebar view/context preset changes.
  // Do not depend on `columns` — Aggregate rebuilds column defs and would wipe CHOOSE COLUMNS selections.
  useEffect(() => {
    if (!presets?.context?.columns) return;

    setColumnVisibility(() => {
      const vis: VisibilityState = {};

      columns.forEach((col) => {
        if (col.id !== undefined) {
          vis[col.id] = presets.context?.columns.includes(col.id) ?? false;
        }
      });
      return vis;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit columns
  }, [presets?.context?.id]);

  useEffect(() => {
    if (isFetching) {
      const columnFilters = mainRenderedTable.getState().columnFilters;
      // The logic isn’t designed for fully generic presets that filter rows yet.
      // Currently, only the Manco/Exposure pages require this,
      // so it checks whether the preset button exists. If it does, it maintains the column filter; otherwise, it doesn't.
      //
      // Revisit the filter preset logic in the future if more control is needed.

      const shouldSkipReset = columnFilters.some(({ id, value }) =>
        presets?.allPresets?.some(
          (preset) =>
            preset.filter?.columnId === id &&
            Array.isArray(value) &&
            value.includes(preset.filter?.value),
        ),
      );

      if (!shouldSkipReset) {
        mainRenderedTable.resetColumnFilters();
      }
    }
  }, [isFetching]);

  // REFACTOR THIS COMPUTER CODE MR BARNEVELD
  const [columnOrder, setColumnOrder] = React.useState<string[]>(
    getColumnOrder(columns, exclusions || [], isEditable),
  );

  // TODO: This is legacy code for the export pdf - leave untouched for now - rethink later
  // function to get an array of chosen column id's in selected order
  const getChosenColumsInOrder = (): string[] => {
    const accurateColumnOrder = getColumnOrder(
      columns,
      exclusions || [],
      isEditable,
    );
    const orderedChoices: string[] = [];
    accurateColumnOrder.forEach((col) => {
      // [ pending removal ] 04/11/2022 Tom Walsh
      // this code relates to notes column which we have chosen to temporarily remove, maybe making permanent
      // if (columnVisibility[col] === true && col !== 'all' && col !== 'notes') {
      //   orderedChoices.push(col);
      // }

      // replacement code
      if (columnVisibility[col] === true && col !== 'index') {
        orderedChoices.push(col);
      }
      //
    });

    return orderedChoices;
  };

  // reorder columns after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        return arrayMove(columnOrder, oldIndex, newIndex); //this is just a splice util
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();
  const [data, setData] = React.useState(originalData); // issues with rerendering on mutation invalidation

  const previousDataRef = useRef(originalData);

  useEffect(() => {
    // Sync with new data if originalData changes meaningfully
    if (!isEqual(previousDataRef.current, originalData)) {
      setData(originalData); // Update state
      previousDataRef.current = originalData; // Update reference
    }
  }, [originalData]);

  const isDataEqualToOriginal = isEqual(data, originalData);

  useEffect(() => {
    if (onDataComparison) {
      onDataComparison(isDataEqualToOriginal);
    }
  }, [isDataEqualToOriginal, onDataComparison]);

  const hideCells =
    !isEditable ||
      selectedPreset?.id === 'default' ||
      selectedPreset === undefined
      ? false
      : true;

  const filteredHiddenData = useMemo(() => {
    // Hide cells if not editable or if no preset is selected

    if (!hideCells) return data;

    return data.filter((row) => {
      const editableFieldKeys = row.editable_fields.map(
        (field) => field.key_name,
      );

      return (
        selectedPreset?.columns.every((column) => {
          return (
            (exclusions || []).includes(column as ColumnFields) ||
            editableFieldKeys.includes(column)
          );
        }) ?? false
      );
    });
  }, [data, selectedPreset, hideCells, exclusions]);

  const mainRenderedTable = useReactTable<T>({
    data: filteredHiddenData,
    columns,
    state: {
      columnVisibility,
      columnOrder,
      globalFilter,
      expanded,
    },

    initialState: {
      columnPinning: {
        left: ['index'],
      },
    },

    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,

    filterFns: {
      fuzzy: fuzzyFilter, // Define as a filter function that can be used in column definitions
    },

    sortingFns: {
      customSortingFn: customSortingFn, // Add custom sorting function
    },

    autoResetPageIndex,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'fuzzy', // Apply fuzzy filter to the global filter (most common use case for fuzzy filter)

    columnResizeMode: 'onChange',
    onColumnOrderChange: setColumnOrder,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),

    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),

    meta: {
      updateData: (rowIndex, columnId, value, resetOld) => {
        // Skip page index reset until after next rerender

        if (resetOld) {
          setData((old) =>
            old.map((row, index) => {
              if (index === rowIndex) {
                return {
                  ...old[rowIndex]!,
                  [columnId]: value,
                };
              }
              return row;
            }),
          );
          return;
        }

        skipAutoResetPageIndex();
        setData((old) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              const updatedRow = {
                ...old[rowIndex]!,
                [columnId]: {
                  old: getOldValue(old[rowIndex]![columnId]),
                  updated: value,
                },
              };
              return updatedRow;
            }
            return row;
          }),
        );
      },
    },
  });

  // Reset expansion state when resetExpansion value changes (e.g., when aggregation toggles)
  // This ensures all nested tables are closed when the data structure changes
  useEffect(() => {
    if (resetExpansion !== undefined) {
      setExpanded({});
    }
  }, [resetExpansion]);

  // This is not an optimal solution to reponsive cells
  // TODO: Refer to reactmaterialtable to understand responsive css function patterns
  useEffect(() => {
    // Call handleResize on first load
    const handleResize = () => {
      // Max the minimum divison by 14 columns to avoid hyper squishing
      const totalShownColumns = Math.min(countTrueValues(columnVisibility), 14); // This causes the sizing issues to make generic I need to pass the minus window with and the minum division

      // 100px estimated padding + index size
      const MIN_SIZE = (window.innerWidth - 100) / totalShownColumns;
      const newColumnSizes: ColumnSizingState = {};

      Object.keys(columnVisibility).forEach((columnId: string) => {
        if (columnVisibility[columnId]) {
          if (columnId === 'name') {
            newColumnSizes[columnId] = Math.max(100, MIN_SIZE);
          } else {
            newColumnSizes[columnId] = MIN_SIZE;
          }
        }
      });

      // This is the only way to update the column sizes
      // Listed here setSize function required in future - https://github.com/TanStack/table/discussions/5558
      mainRenderedTable.setColumnSizing(() => {
        const obj: Record<string, number> = {};
        columns.forEach((col) => {
          if (col.id !== undefined && col.id !== 'index') {
            obj[col.id] = newColumnSizes[col.id];
          }
        });
        return obj;
      });
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [columnVisibility, mainRenderedTable]);

  // function to convert react-table row model data to export ready data

  const convertRowModelForExport = (rows: Row<T>[]) => {
    return rows.map((row) => row.original);
  };

  // function to handle pdf export
  const tablePdfExportHandler = async () => {
    const renderColumns: any = [];
    const chosenColumns = getChosenColumsInOrder();
    chosenColumns.map((chosCol) => {
      const item = columns.filter((col) => col.id === chosCol)[0];
      renderColumns.push({
        header: item.header,
        dataKey: item.id,
        render: item.cell,
      });
    });
    return {
      startY: 50,
      columns: renderColumns,
      body: convertRowModelForExport(mainRenderedTable.getRowModel().rows),
    };
  };

  const tableFiltered =
    (mainRenderedTable.getState().columnFilters.length &&
      mainRenderedTable.getState().columnFilters.length > 0) ||
    mainRenderedTable.getState().globalFilter;

  const [pdfIdentifier] = useState<string>(`${pdfConfig?.pdfIdentifier}`);

  // When column selections update, dispatch changes in pdf export data to redux
  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: pdfIdentifier,
        handler: tablePdfExportHandler,
        type: PdfComponentType.TABLE,
        title: `${pdfConfig?.pdfTitle || pdfConfig?.pdfIdentifier}${tableFiltered ? '(Filtered)' : ''
          }`,
        dontMoveToNewPage: true,
        groupName: pdfConfig?.pdfExportGroupName ?? undefined,
        groupOrder: pdfConfig?.pdfExportGroupOrder ?? undefined,
      }),
    );
  }, [columns, columnOrder, tableFiltered, columnVisibility]);

  // Hide cells if not editable or if no preset is selected

  // Virtualiser
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: mainRenderedTable.getSortedRowModel().rows.length,
    estimateSize: () => 44, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    // measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== 'undefined' &&
        navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  function handleResetFilters() {
    setSelectedPreset(presets?.default);
    setColumnVisibility(defaultColumnVisibility);
    mainRenderedTable.resetColumnFilters();
  }

  const test = mainRenderedTable.getVisibleLeafColumns().length - 1; // this is because 1px is added to each column to account for border right - its not ideal but it works - figure better css solution

  const tableWidth = mainRenderedTable.getCenterTotalSize() - test - 10;

  function handleSelectedPreset<T>(
    selected: boolean,
    preset: UltraTablePreset,
    columns: ColumnDef<T>[],
  ) {
    if (!presets) return; // Bail out if not ready no presets - this func cant be called if there are none anyway lolz

    const visibilityFromPreset = (
      preset: UltraTablePreset,
    ): VisibilityState => {
      const vis: VisibilityState = {};
      columns.forEach((col) => {
        const colId = col.id!;
        vis[colId] = preset.columns.includes(colId);
      });
      return vis;
    };

    if (preset.filter) {
      const column = mainRenderedTable.getColumn(preset.filter.columnId);
      if (column) {
        column.setFilterValue(
          selected ? undefined : [preset.filter.value ?? preset.name],
        );
      }
    }

    if (selected) {
      setSelectedPreset(presets?.default);
      setColumnVisibility(defaultColumnVisibility);
    } else {
      setSelectedPreset(preset);
      setColumnVisibility(visibilityFromPreset(preset));
    }
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <GridItem xs={12} card>
        <div className={classes.toolbar}>
          <h2 className={classes.header}>
            {title} {tableFiltered ? '(Filtered)' : ''}
          </h2>

          <div className={classes.toolbarOptions}>
            <div className="flex flex-grow items-center justify-center gap-x-2 gap-y-0.5 md:justify-end">
              {presets &&
                presets.display === 'buttons' &&
                presets.allPresets.map((preset) => {
                  const columns = mainRenderedTable.getAllLeafColumns();
                  const selected = !!preset && selectedPreset?.id === preset.id;

                  return (
                    <PresetSelectionBtn
                      preset={preset}
                      selected={selected}
                      onClick={() => {
                        handleSelectedPreset(selected, preset, columns);
                      }}
                    >
                      {preset.name}
                    </PresetSelectionBtn>
                  );
                })}

              {presets && presets.display === 'dropdown' && (
                <div>
                  <GenericDropdown
                    data={presets.allPresets}
                    setSelected={(preset) => {
                      const columns = mainRenderedTable.getAllLeafColumns();
                      const selected = selectedPreset?.id === preset.id;
                      handleSelectedPreset(selected, preset, columns);
                    }}
                    tooltip="Select A Preset"
                    selected={selectedPreset}
                    getDisplayValue={(item) => item.name}
                    label={selectedPreset?.name || 'Select a Preset'}
                  />
                </div>
              )}

              <SearchBar debounceTime={300} searchHandler={setGlobalFilter} />
            </div>

            <div className={classes.mainOptionsContainer}>
              <div className={classes.mainOptions}>
                {actionBtns &&
                  actionBtns.map((btn, index) => (
                    <Fragment key={index}>{btn}</Fragment>
                  ))}

                {columnSelector && (
                  <ColumnPopupSelector
                    table={mainRenderedTable}
                    handleResetFilters={handleResetFilters}
                    setSelectedPreset={setSelectedPreset}
                  />
                )}

                <Button
                  aria-describedby={'reset'}
                  variant="text"
                  disableElevation
                  onClick={handleResetFilters}
                  className={classes.resetButton}
                >
                  Reset Filters
                </Button>
                <ExportButton
                  exportData={convertRowModelForExport(
                    mainRenderedTable.getRowModel().rows,
                  )}
                  pdfIdentifier={`${pdfIdentifier}`}
                  fields={getChosenColumsInOrder()}
                  fileName={pdfConfig?.pdfFileName || pdfConfig?.pdfIdentifier}
                  selectedPositionDate={positionDate}
                  allowPdfExport={true}
                />
                {toolBarBtns && <PortalTools toolBarBtns={toolBarBtns} />}
              </div>
            </div>
          </div>
        </div>

        {isEditable && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Legend
              className="mb-2"
              values={[
                'Editable cells',
                'Update implemented. (Will be reflected in calculations the next time the fund is run.)',
              ]}
            />
          </div>
        )}

        <div
          ref={tableContainerRef}
          className={clsx(classes.tableContainer, tableContainerClassName)}
        >
          {(isFetching || isAggregatePending) && (
            <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-white/70">
              <RaptorLoading />
            </div>
          )}
          <table
            className={classes.table}
            style={{
              margin: 'auto',
              display: 'grid',
              width: tableWidth,
            }}
          >
            <thead
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'white',
                width: tableWidth,
              }}
            >
              {mainRenderedTable.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className={classes.headerRow}
                  style={{ display: 'flex', width: '100%' }}
                >
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) =>
                      header.id !== 'index' ? (
                        <DraggableTableHeader
                          key={header.id}
                          header={header}
                          classes={classes}
                          table={mainRenderedTable}
                        />
                      ) : (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          className={classes.expanderHead}
                          style={{ ...getCommonPinningStyles(header.column) }}
                        >
                          {header.isPlaceholder ? null : (
                            <>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </>
                          )}
                        </th>
                      ),
                    )}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            <tbody
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`, // tells scrollbar how big the table is
                position: 'relative', // needed for absolute positioning of rows
                width: tableWidth,
              }}
            >
              {rowVirtualizer.getVirtualItems().length ? (
                rowVirtualizer.getVirtualItems().map((virtualRow, index) => {
                  const row = mainRenderedTable.getSortedRowModel().rows[
                    virtualRow.index
                  ] as Row<T>;

                  return (
                    <Fragment key={row.id}>
                      <tr
                        data-index={virtualRow.index} // needed for dynamic row height measurement
                        ref={(node) => {
                          // This seems to cause the issue, unsure how to proceed other than generic try catch.
                          // Measure measure ments can sometimes fail

                          // BUG: Hasty resolver: - Potentially alot of research required
                          // Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.

                          if (node) {
                            try {
                              rowVirtualizer.measureElement(node);
                            } catch (e) {
                              console.warn('measureElement failed:', e);
                            }
                          }
                        }}
                        className="flex border-t border-gray-200 hover:bg-gray-100"
                        style={{
                          position: 'absolute',
                          transform: `translateY(${virtualRow.start}px)`,
                          width: '100%',
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={classes.cell}
                            style={{
                              width:
                                mainRenderedTable.getState().columnSizing[
                                cell.column.id
                                ],
                              ...getCommonPinningStyles(cell.column),
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                      {row.getIsExpanded() && (
                        <div
                          style={{
                            width: '100%',
                            position: 'relative',
                            transform: `translateY(${virtualRow.start + virtualRow.size
                              }px)`,
                          }}
                        >
                          {/* <td
                            colSpan={row.getVisibleCells().length}
                            style={{ width: '100%' }}
                          > */}
                          {NestedComponent && (
                            <NestedComponent
                              rowIndex={row.index}
                              data={row.original}
                              initialOffset={() =>
                                rowVirtualizer.scrollOffset ?? 0
                              }
                              getScrollElement={() =>
                                tableContainerRef.current as HTMLDivElement
                              }
                              scrollMargin={virtualRow.start}
                            />
                          )}
                          {/* </td> */}
                        </div>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td
                    style={{
                      width: tableWidth,
                    }}
                    className={classes.noDataMessage}
                  >
                    No Matches For Current Filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GridItem>
    </DndContext>
  );
};

export default UltraTable;
